/**
 * Guards against the React-vs-browser-translation DOM crash — the
 * "NotFoundError: Failed to execute 'removeChild'/'insertBefore' on 'Node':
 * The node to be removed is not a child of this node." family (Sentry
 * WEBAPP-CC7 and its long-standing siblings WEBAPP-8RS / BVY / BR2 / BN5).
 *
 * Browser page-translation (Chrome / Edge "Translate this page") swaps the
 * text nodes React manages for <font>-wrapped copies, moving the original node
 * out from under the parent React still tracks. When React later unmounts or
 * reorders that subtree it calls `parent.removeChild(node)` or
 * `parent.insertBefore(node, ref)` against a node the translator already
 * relocated, and the browser throws a NotFoundError (DOMException code 8)
 * mid-commit — tearing down the React tree and reaching Sentry via the console
 * integration.
 *
 * The React team's recommended mitigation (facebook/react#11538) makes these
 * two methods tolerant of that exact desync: removing a node that isn't ours is
 * a no-op (it's already detached), and inserting before a relocated reference
 * falls back to an append. Every valid operation still delegates to the native
 * method unchanged, so first-party behavior is untouched.
 */

type RemoveChildFn = <T extends Node>(child: T) => T;
type InsertBeforeFn = <T extends Node>(node: T, child: Node | null) => T;

export function guardedRemoveChild(native: RemoveChildFn): RemoveChildFn {
  return function removeChild<T extends Node>(this: Node, child: T): T {
    // Skip the native call when the translator already detached `child`;
    // otherwise remove it as normal. removeChild returns the removed node.
    if (child.parentNode === this) {
      native.call(this, child);
    }
    return child;
  };
}

export function guardedInsertBefore(native: InsertBeforeFn): InsertBeforeFn {
  return function insertBefore<T extends Node>(this: Node, node: T, child: Node | null): T {
    // Append (null reference) when the translator relocated the reference node;
    // otherwise insert as normal. insertBefore returns the inserted node.
    const ref = child && child.parentNode !== this ? null : child;
    native.call(this, node, ref);
    return node;
  };
}

let installed = false;

/**
 * Wraps Node.prototype.removeChild / insertBefore with the guards above. Call
 * once, before React renders (see src/index.tsx). Idempotent and a no-op in
 * non-DOM environments.
 */
export function installTranslationDomGuard(): void {
  if (installed || typeof Node === 'undefined') return;
  installed = true;
  Node.prototype.removeChild = guardedRemoveChild(Node.prototype.removeChild);
  Node.prototype.insertBefore = guardedInsertBefore(Node.prototype.insertBefore);
}
